/**
 * funChallengesRoyaleStore.ts
 *
 * N-player FSM + live state for Puzzle Royale. SEPARATE from the 1v1
 * `funChallengesBattleStore` so the shipped ST/PTB/PDB battles stay frozen —
 * royale tracks up to 100 players in a `Record<userId, PlayerRoyaleState>`,
 * a derived leaderboard, a shared puzzle sequence, and lobby state.
 *
 * FSM:
 *   idle ── setLobby ─────────────► lobby
 *   lobby ── startSession ────────► in_battle
 *   in_battle ── endRoyale ───────► ended
 *   * ── reset ────────────────────► idle
 *
 * Source-of-truth model (mirrors the proven 1v1 store):
 *   The server echoes every fun_move_broadcast back to the mover too, so
 *   `applyMoveBroadcast` runs for self AND others. We trust the server's
 *   cumulative `correct`/`wrong`/`combo`/`points` and use `nextIndex` for
 *   idempotency (a re-delivered broadcast whose nextIndex ≤ our recorded
 *   move count is a no-op). Combo/points are backfilled via royaleScoring
 *   when an older server omits them.
 *
 * The socket layer (Phase 5) routes royale-gameType fun_* messages here; the
 * lobby/battle UI (Phases 2-3) can also drive it via a dev mock until then.
 */

import {create} from 'zustand';

import type {
  PlayerRoyaleState,
  RoyaleEndPayload,
  RoyaleLobby,
  RoyaleLobbyPlayer,
  RoyaleSession,
  RoyaleStatus,
} from '@/Screens/FunChallenges/royaleTypes';
import {
  applyMove,
  bonusForCombo,
  pieceForPoints,
  rankLeaderboard,
  recomputeDerived,
} from '@/Screens/FunChallenges/util/royaleScoring';

/** Build a fresh per-player state row. */
function emptyPlayer(
  userId: string,
  name: string,
  avatarUrl?: string,
): PlayerRoyaleState {
  return {
    userId,
    name,
    avatarUrl,
    points: 0,
    solved: 0,
    wrong: 0,
    combo: 0,
    bestCombo: 0,
    piece: 'pawn',
    isLeader: false,
    eliminated: false,
    myIndex: 0,
    reachedAt: 0,
  };
}

interface RoyaleState {
  status: RoyaleStatus;
  lobby: RoyaleLobby | null;
  session: RoyaleSession | null;
  /** Live per-player state, keyed by userId. Up to ENGINE_MAX_PLAYERS rows. */
  players: Record<string, PlayerRoyaleState>;
  /** Derived ranking (winner first); recomputed on every score change. */
  leaderboard: string[];
  /**
   * Player ids in stable JOIN ORDER (lobby roster order, then session
   * participant order). Used to assign join-order colours (royaleColors
   * .buildColorMap) — distinct from `leaderboard`, which is by rank.
   */
  joinOrder: string[];
  myUserId: string | null;
  /** Web spectate: the userId whose board we're following (null = not spectating). */
  spectating: string | null;
  /**
   * True when THIS client is a spectator (no scoring, read-only board) rather
   * than a participant. Set by the spectate bootstrap (fun_spectate_start) or a
   * late join routed to spectate. Drives the read-only spectate view.
   */
  isSpectator: boolean;
  /**
   * Per-player response trails — ordered `<puzzleId>:<uci>:<0|1>` triples.
   * Populated on hydrate/spectate-bootstrap and appended on every move
   * broadcast that carries `square`. The spectate view reconstructs a followed
   * player's last move from the tail of their trail. Players don't need this
   * (they see their own board live), so it's only filled for spectators.
   */
  trails: Record<string, string[]>;
  /**
   * Spectator roster (server-broadcast), keyed nowhere — a flat list of who's
   * watching. Distinct from `players`; shown in the Spectators rail tab. Names
   * arrive via fun_room_update / fun_spectate_start / fun_session_start.
   */
  spectators: Array<{userId: string; name: string; avatarUrl?: string}>;
  /**
   * Spectate per-ply live boards (server-streamed). Keyed by playerId → the
   * most recent ply: fen, from/to, the player's locked board orientation, the
   * ply kind ('setup'|'player'|'reply'), and a monotonic `seq` so the spectate
   * view can react to repeated identical FENs. The spectate view renders the
   * followed player's entry so pieces animate move-by-move.
   */
  liveBoards: Record<
    string,
    {
      fen: string;
      from: string;
      to: string;
      orientation: 'white' | 'black';
      kind: 'setup' | 'player' | 'reply' | 'wrong';
      seq: number;
    }
  >;
  /**
   * Classroom origin, if this royale was created by a coach from the classroom.
   * Set when the coach auto-creates (the FunChallenges lobby tab consumes the
   * classroom handoff) and preserved across lobby → battle → REMATCH so the
   * host's tab can register a rematch lobby with Django (notifying students).
   * Null for public (non-classroom) royales.
   */
  classroomContext: {meetingId: string; occurenceId: string} | null;
  endResult: RoyaleEndPayload | null;
  errorMessage: string | null;
  errorCode: string | null;
}

interface RoyaleActions {
  // ── Lobby ───────────────────────────────────────────────────────────────
  setLobby: (lobby: RoyaleLobby, myUserId: string) => void;
  /** Mark this royale as classroom-originated (preserved across rematch). */
  setClassroomContext: (
    ctx: {meetingId: string; occurenceId: string} | null,
  ) => void;
  upsertLobbyPlayers: (players: RoyaleLobbyPlayer[]) => void;
  /** Host toggles play-vs-spectate in the waiting room (before start). */
  setHostIsPlayer: (hostIsPlayer: boolean) => void;
  setSpectating: (userId: string | null) => void;
  /** Replace the spectator roster (server-broadcast). */
  setSpectators: (
    spectators: Array<{userId: string; name: string; avatarUrl?: string}>,
  ) => void;
  /** Record a player's latest streamed ply (spectate live board). */
  setLiveBoard: (
    playerId: string,
    fen: string,
    from: string,
    to: string,
    orientation: 'white' | 'black',
    kind: 'setup' | 'player' | 'reply' | 'wrong',
  ) => void;
  /**
   * Seed a WAITING-room lobby for a pre-start spectator (fun_room_update with
   * forSpectator). Sets isSpectator so the waiting room renders read-only and
   * the user transitions to the live spectate view when the host starts.
   */
  setLobbyAsSpectator: (lobby: RoyaleLobby, myUserId: string) => void;

  // ── Spectate (web) ────────────────────────────────────────────────────────
  /**
   * Bootstrap a SPECTATOR into a live race (fun_spectate_start). Same hydration
   * as hydrateRoyale but flags `isSpectator` so the screen is read-only, and
   * auto-selects a player to follow (the current leader). Stores per-player
   * trails so the followed board reconstructs the player's last move.
   */
  hydrateSpectate: (
    session: RoyaleSession,
    players: RoyaleLobbyPlayer[],
    allResponses: Record<string, string[]>,
    scores: Record<
      string,
      {points: number; solved: number; wrong: number; bestCombo: number}
    >,
  ) => void;

  // ── Session lifecycle ─────────────────────────────────────────────────────
  startSession: (
    session: RoyaleSession,
    players: RoyaleLobbyPlayer[],
    nowMs: number,
  ) => void;
  endRoyale: (result: RoyaleEndPayload) => void;

  // ── In-battle updates ─────────────────────────────────────────────────────
  /**
   * Apply a (self or other) move broadcast. Server is source of truth:
   * `correct`/`wrong` are cumulative; `combo`/`points` come from the server
   * when present, else are recomputed. `nextIndex` guards idempotency.
   * `nowMs` stamps reachedAt for tie-breaks (pass Date.now() at call site —
   * the store stays pure/deterministic for tests by taking it as an arg).
   */
  applyMoveBroadcast: (
    playerId: string,
    correct: number,
    wrong: number,
    nextIndex: number,
    nowMs: number,
    combo?: number,
    points?: number,
    /** Echoed `<puzzleId>:<uci>:<0|1>` triple — appended to the player's trail
     *  (only the server sends this to spectators + the mover's own echo). */
    square?: string,
  ) => void;
  markEliminated: (userId: string) => void;

  // ── Bulk hydration (fun_session_rejoin) ───────────────────────────────────
  hydrateRoyale: (
    session: RoyaleSession,
    players: RoyaleLobbyPlayer[],
    allResponses: Record<string, string[]>,
    scores: Record<
      string,
      {points: number; solved: number; wrong: number; bestCombo: number}
    >,
  ) => void;

  // ── Errors ────────────────────────────────────────────────────────────────
  setError: (message: string, code?: string) => void;
  clearError: () => void;

  reset: () => void;
}

const initialState: RoyaleState = {
  status: 'idle',
  lobby: null,
  session: null,
  players: {},
  leaderboard: [],
  joinOrder: [],
  myUserId: null,
  spectating: null,
  isSpectator: false,
  trails: {},
  spectators: [],
  liveBoards: {},
  classroomContext: null,
  endResult: null,
  errorMessage: null,
  errorCode: null,
};

/** Recompute derived fields + leaderboard from a players map. */
function withDerived(players: Record<string, PlayerRoyaleState>) {
  const derived = recomputeDerived(players);
  return {players: derived, leaderboard: rankLeaderboard(derived)};
}

/**
 * Merge `incoming` ids onto an existing join-order list, PRESERVING the order
 * of ids already present and appending genuinely new ones at the end. Keeps
 * colour assignment stable as the roster grows (a new joiner never reshuffles
 * everyone's colour).
 */
function mergeJoinOrder(prev: string[], incoming: string[]): string[] {
  const seen = new Set(prev);
  const next = prev.slice();
  for (const id of incoming) {
    const key = String(id);
    if (!seen.has(key)) {
      seen.add(key);
      next.push(key);
    }
  }
  return next;
}

const useFunChallengesRoyaleStore = create<RoyaleState & RoyaleActions>(
  set => ({
    ...initialState,

    setLobby: (lobby, myUserId) =>
      set(prev => ({
        ...initialState,
        status: 'lobby',
        lobby,
        joinOrder: mergeJoinOrder(
          [],
          (lobby.players || []).map(p => String(p.userId)),
        ),
        myUserId,
        // Keep classroom origin across re-seeds (incl. the rematch lobby) so the
        // host's tab still knows to register a rematch with Django.
        classroomContext: prev.classroomContext,
      })),

    setClassroomContext: ctx => set({classroomContext: ctx}),

    upsertLobbyPlayers: players =>
      set(prev => {
        if (!prev.lobby) {
          return prev;
        }
        return {
          lobby: {...prev.lobby, players},
          joinOrder: mergeJoinOrder(
            prev.joinOrder,
            players.map(p => String(p.userId)),
          ),
        };
      }),

    setHostIsPlayer: hostIsPlayer =>
      set(prev => {
        if (!prev.lobby) {
          return prev;
        }
        return {lobby: {...prev.lobby, hostIsPlayer}};
      }),

    setSpectating: userId => set({spectating: userId}),

    setSpectators: spectators => set({spectators: spectators || []}),

    setLiveBoard: (playerId, fen, from, to, orientation, kind) =>
      set(prev => {
        const prevSeq = prev.liveBoards[String(playerId)]?.seq ?? 0;
        return {
          liveBoards: {
            ...prev.liveBoards,
            [String(playerId)]: {
              fen,
              from,
              to,
              orientation,
              kind,
              seq: prevSeq + 1,
            },
          },
        };
      }),

    setLobbyAsSpectator: (lobby, myUserId) =>
      set({
        ...initialState,
        status: 'lobby',
        lobby,
        joinOrder: mergeJoinOrder(
          [],
          (lobby.players || []).map(p => String(p.userId)),
        ),
        myUserId,
        isSpectator: true,
      }),

    startSession: (session, players, nowMs) =>
      set(prev => {
        const map: Record<string, PlayerRoyaleState> = {};
        players.forEach(p => {
          map[p.userId] = {
            ...emptyPlayer(p.userId, p.name, p.avatarUrl),
            reachedAt: nowMs,
          };
        });
        // Preserve the lobby's join order, then append any session-only ids.
        const joinOrder = mergeJoinOrder(
          prev.joinOrder,
          players.map(p => String(p.userId)),
        );
        return {
          ...initialState,
          status: 'in_battle',
          session,
          joinOrder,
          myUserId: session.myUserId,
          // Preserve spectator state across the lobby→battle transition. The
          // server's session-start payload doesn't always re-send the spectator
          // roster, and `...initialState` would otherwise wipe it — silently
          // dropping spectator names the moment the game starts. (The session
          // routing still overlays payload.spectators when present.)
          spectators: prev.spectators,
          isSpectator: prev.isSpectator,
          spectating: prev.spectating,
          // Keep classroom origin through the battle (needed if a rematch
          // follows — see setLobby).
          classroomContext: prev.classroomContext,
          ...withDerived(map),
        };
      }),

    applyMoveBroadcast: (
      playerId,
      correct,
      wrong,
      nextIndex,
      nowMs,
      combo,
      points,
      square,
    ) =>
      set(prev => {
        const existing =
          prev.players[playerId] ?? emptyPlayer(String(playerId), '');
        // Whether THIS move solved — from the server's rising cumulative.
        const moveSolved = correct > existing.solved;
        // Append this move's entry to the player's trail. The trail is the
        // per-index correct/wrong record the puzzle grid reads, so it must NOT
        // depend on `square` being present (the sender echo sometimes omits it,
        // which left the grid uncoloured while solved/wrong climbed). Prefer the
        // real echoed triple (`square`, carries the puzzleId+uci for the
        // spectate board); otherwise synthesise `<puzzleId>::<bit>` from the
        // shared coords sequence + the known correct/wrong bit. Pad any gap so
        // trail length always reaches nextIndex.
        const appendTrail = (
          trails: Record<string, string[]>,
        ): Record<string, string[]> => {
          const cur = trails[playerId] ?? [];
          if (cur.length >= nextIndex) {
            return trails;
          }
          const coords = prev.session?.coords ?? [];
          const next = [...cur];
          // Pad missing earlier entries (best-effort: mark solved so a dropped
          // echo doesn't paint a false red; the final entry below is exact).
          while (next.length < nextIndex - 1) {
            const idx = next.length;
            next.push(`${coords[idx] ?? ''}::1`);
          }
          // The entry for THIS move (index nextIndex-1): use the real echo when
          // present, else synthesise with the exact bit we just computed.
          if (next.length < nextIndex) {
            const idx = next.length;
            const bit = moveSolved ? '1' : '0';
            next.push(square ?? `${coords[idx] ?? ''}::${bit}`);
          }
          return {...trails, [playerId]: next};
        };
        // Idempotency: nextIndex is the player's total move count. If we've
        // already recorded that many moves, this echo is a duplicate.
        const recordedMoves = existing.solved + existing.wrong;
        if (recordedMoves >= nextIndex) {
          // Still trust the server's cumulative scalars (snap any drift).
          const snapped: PlayerRoyaleState = {
            ...existing,
            solved: correct,
            wrong,
            myIndex: nextIndex,
          };
          return {
            ...withDerived({...prev.players, [playerId]: snapped}),
            trails: appendTrail(prev.trails),
          };
        }

        // Points + combo: prefer server values; else recompute locally.
        const solved = moveSolved;
        let nextPoints: number;
        let nextCombo: number;
        if (typeof points === 'number' && typeof combo === 'number') {
          nextPoints = points;
          nextCombo = combo;
        } else {
          const r = applyMove(
            {
              points: existing.points,
              combo: existing.combo,
              bestCombo: existing.bestCombo,
            },
            solved,
          );
          nextPoints = r.points;
          nextCombo = r.combo;
        }
        const bestCombo = Math.max(existing.bestCombo, nextCombo);
        // reachedAt advances only when points changed (tie-break = first-to-reach).
        const reachedAt =
          nextPoints !== existing.points ? nowMs : existing.reachedAt;

        const updated: PlayerRoyaleState = {
          ...existing,
          solved: correct,
          wrong,
          combo: nextCombo,
          bestCombo,
          points: nextPoints,
          piece: pieceForPoints(nextPoints),
          myIndex: nextIndex,
          reachedAt,
        };
        return {
          ...withDerived({...prev.players, [playerId]: updated}),
          trails: appendTrail(prev.trails),
        };
      }),

    markEliminated: userId =>
      set(prev => {
        const p = prev.players[userId];
        if (!p) {
          return prev;
        }
        return withDerived({
          ...prev.players,
          [userId]: {...p, eliminated: true},
        });
      }),

    hydrateRoyale: (session, players, allResponses, scores) =>
      set(prev => {
        const map: Record<string, PlayerRoyaleState> = {};
        players.forEach(p => {
          const trail = allResponses[p.userId] ?? [];
          const score = scores[p.userId];
          // Prefer the server's authoritative scores; fall back to trail length.
          const solved =
            score?.solved ?? trail.filter(r => r.split(':')[2] === '1').length;
          const wrong =
            score?.wrong ?? trail.filter(r => r.split(':')[2] === '0').length;
          const points =
            score?.points ?? solved + bonusForComboFromTrail(trail);
          const bestCombo = score?.bestCombo ?? bestComboFromTrail(trail);
          map[p.userId] = {
            ...emptyPlayer(p.userId, p.name, p.avatarUrl),
            solved,
            wrong,
            points,
            bestCombo,
            // combo can't be perfectly reconstructed without ordering certainty
            // across players; trail is per-player ordered, so derive the tail.
            combo: tailComboFromTrail(trail),
            piece: pieceForPoints(points),
            myIndex: solved + wrong,
          };
        });
        const joinOrder = mergeJoinOrder(
          prev.joinOrder,
          players.map(p => String(p.userId)),
        );
        return {
          ...initialState,
          status: 'in_battle',
          session,
          joinOrder,
          myUserId: session.myUserId,
          // CRITICAL: carry the per-player response trails through the rejoin so
          // the puzzle grid renders the real per-index correct/wrong. Without
          // this, trails stayed empty while myIndex was restored → the grid's
          // fallback painted every completed box green.
          trails: {...allResponses},
          ...withDerived(map),
        };
      }),

    hydrateSpectate: (session, players, allResponses, scores) =>
      set(prev => {
        const map: Record<string, PlayerRoyaleState> = {};
        players.forEach(p => {
          const trail = allResponses[p.userId] ?? [];
          const score = scores[p.userId];
          const solved =
            score?.solved ?? trail.filter(r => r.split(':')[2] === '1').length;
          const wrong =
            score?.wrong ?? trail.filter(r => r.split(':')[2] === '0').length;
          const points =
            score?.points ?? solved + bonusForComboFromTrail(trail);
          const bestCombo = score?.bestCombo ?? bestComboFromTrail(trail);
          map[p.userId] = {
            ...emptyPlayer(p.userId, p.name, p.avatarUrl),
            solved,
            wrong,
            points,
            bestCombo,
            combo: tailComboFromTrail(trail),
            piece: pieceForPoints(points),
            myIndex: solved + wrong,
          };
        });
        const joinOrder = mergeJoinOrder(
          prev.joinOrder,
          players.map(p => String(p.userId)),
        );
        const derived = withDerived(map);
        // Auto-follow the current leader so the spectate board isn't blank.
        const follow = derived.leaderboard[0] ?? players[0]?.userId ?? null;
        return {
          ...initialState,
          status: 'in_battle',
          session,
          joinOrder,
          // A spectator's "myUserId" is themselves, but they're not a player —
          // isSpectator gates scoring/board interaction.
          myUserId: session.myUserId,
          isSpectator: true,
          spectating: follow,
          trails: {...allResponses},
          ...derived,
        };
      }),

    setError: (message, code) =>
      set({errorMessage: message, errorCode: code ?? null}),
    clearError: () => set({errorMessage: null, errorCode: null}),

    endRoyale: result =>
      set(prev => {
        // Snap final scores from the end payload onto the players map.
        const next: Record<string, PlayerRoyaleState> = {...prev.players};
        Object.entries(result.scores).forEach(([id, s]) => {
          const p = next[id] ?? emptyPlayer(id, '');
          next[id] = {
            ...p,
            points: s.points,
            solved: s.solved,
            wrong: s.wrong,
            bestCombo: s.bestCombo,
            piece: pieceForPoints(s.points),
          };
        });
        const derived = withDerived(next);
        // The SERVER's rankedPlayers is authoritative — it already places
        // forfeiters/eliminated below everyone who stayed in. Prefer it for the
        // final leaderboard so the client never shows a forfeiter as #1 (a local
        // points-only re-rank would). Fall back to the derived order.
        const finalLeaderboard =
          result.rankedPlayers && result.rankedPlayers.length
            ? result.rankedPlayers.map(String).filter(id => derived.players[id])
            : derived.leaderboard;
        return {
          status: 'ended',
          endResult: result,
          // Explicitly carry these through so the ended state never loses the
          // spectator roster / identity / session (#5: spectator name was
          // falling back to "Spectator" on end). zustand merges, but being
          // explicit guards against any future full-replace refactor.
          spectators: prev.spectators,
          myUserId: prev.myUserId,
          isSpectator: prev.isSpectator,
          session: prev.session,
          players: derived.players,
          leaderboard: finalLeaderboard,
        };
      }),

    reset: () => set({...initialState}),
  }),
);

// ── Trail reconstruction helpers (rejoin) ─────────────────────────────────────
// A per-player response trail is an ordered array of `<puzzleId>:<uci>:<0|1>`.
// The suffix bit is solve(1)/wrong(0). These derive combo/points from it.

function bestComboFromTrail(trail: string[]): number {
  let best = 0;
  let cur = 0;
  for (const r of trail) {
    if (r.split(':')[2] === '1') {
      cur += 1;
      if (cur > best) {
        best = cur;
      }
    } else {
      cur = 0;
    }
  }
  return best;
}

function tailComboFromTrail(trail: string[]): number {
  let cur = 0;
  for (const r of trail) {
    cur = r.split(':')[2] === '1' ? cur + 1 : 0;
  }
  return cur;
}

/** Sum of streak bonuses earned along the trail (replays applyMove). */
function bonusForComboFromTrail(trail: string[]): number {
  let combo = 0;
  let bonus = 0;
  for (const r of trail) {
    if (r.split(':')[2] === '1') {
      combo += 1;
      // bonusForCombo gives cumulative-at-combo; the per-step delta is the
      // milestone bonus hit exactly at this combo value.
      const prevBonus = bonusForCombo(combo - 1);
      bonus += bonusForCombo(combo) - prevBonus;
    } else {
      combo = 0;
    }
  }
  return bonus;
}

export default useFunChallengesRoyaleStore;
